import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../context/AuthContext";
import authService from "../services/authService";

const Unsubscribe = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError("");
    
    try {
      // TODO: Implement unsubscribe API call here
      console.log("Unsubscribe data:", data);
      const response = await authService.unsubscribe(data);
      console.log("Unsubscribe response:", response);
      if(response.success){
        alert("You have been successfully unsubscribed from our services.");
        logout();
        navigate("/");
      }else{
        setError(response.message);
      }

    } catch (err) {
      setError(err.response?.data?.message || 'Unsubscribe failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
        py: { xs: 4, md: 8 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)",
          zIndex: 0,
        },
      }}
    >
      {/* Navigation Buttons */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 10,
          display: "flex",
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => navigate("/")}
          sx={{
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: "white" },
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: "white" },
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                },
              }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  fontWeight: 700,
                  color: "#333",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "50px",
                    height: "3px",
                    background: "linear-gradient(90deg, #5c0f2f, #7d1f4f)",
                    borderRadius: "2px",
                  },
                }}
              >
                Unsubscribe
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  color: "#666",
                  lineHeight: 1.6,
                }}
              >
                We're sorry to see you go. Please enter your email and password to confirm your unsubscribe request.
              </Typography>

              {error && (
                <Typography
                  color="error"
                  sx={{
                    textAlign: "center",
                    mb: 2,
                    p: 1,
                    bgcolor: "rgba(255, 0, 0, 0.1)",
                    borderRadius: "4px",
                  }}
                >
                  {error}
                </Typography>
              )}
              
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register("email", { 
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#5c0f2f",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#5c0f2f",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5c0f2f",
                    },
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  {...register("password", { 
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters"
                    }
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#5c0f2f",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#5c0f2f",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5c0f2f",
                    },
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    background: "linear-gradient(45deg, #dc3545, #c82333)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #c82333, #bd2130)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 15px rgba(220,53,69,0.3)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(45deg, #dc3545, #c82333)",
                      opacity: 0.7,
                    },
                    fontSize: "1.1rem",
                    textTransform: "none",
                    borderRadius: "8px",
                    transition: "all 0.3s",
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: "white" }} />
                      Processing...
                    </Box>
                  ) : (
                    "Unsubscribe"
                  )}
                </Button>
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate("/")}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderColor: "#5c0f2f",
                    color: "#5c0f2f",
                    "&:hover": {
                      borderColor: "#4d0d28",
                      backgroundColor: "rgba(92,15,47,0.04)",
                    },
                    fontSize: "1rem",
                    textTransform: "none",
                    borderRadius: "8px",
                    transition: "all 0.3s",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Unsubscribe; 